import { ChangeEvent, KeyboardEvent, useRef } from 'react';
import cx from 'classnames';

import { controlSurfaceClasses, useField } from '../field';

export interface CodeInputProps {
  /** One digit per box, `''` for an empty one; its length is the number of boxes. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Read by screen readers for the group; each box adds its number. */
  label: string;
  invalid?: boolean;
}

/** A one-time code, a box per digit: typing moves on, Backspace in an empty box moves back, pasting fills all. */
export const CodeInput = ({ value, onChange, label, invalid }: CodeInputProps) => {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const field = useField();
  const isInvalid = Boolean(invalid || field?.invalid);
  const length = value.length;

  const handleChange = (event: ChangeEvent<HTMLInputElement>, idx: number) => {
    const typed = event.target.value;
    if (typed.length === length && /^\d+$/.test(typed)) {
      onChange(typed.split(''));
      event.target.blur();
      return;
    }
    if (!/^\d?$/.test(typed)) return;
    onChange(value.map((digit, i) => (i === idx ? typed : digit)));
    if (!typed) return;
    if (idx + 1 < length) boxes.current[idx + 1]?.focus();
    else event.target.blur();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (event.key === 'Backspace' && !value[idx] && idx > 0) boxes.current[idx - 1]?.focus();
  };

  return (
    <div role="group" aria-label={label} className="flex justify-center gap-2.5">
      {value.map((digit, idx) => (
        <input
          key={idx}
          ref={(box) => {
            boxes.current[idx] = box;
          }}
          type="tel"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          aria-label={`${label}, цифра ${idx + 1}`}
          aria-invalid={isInvalid || undefined}
          aria-describedby={field?.describedBy}
          value={digit}
          onChange={(event) => handleChange(event, idx)}
          onKeyDown={(event) => handleKeyDown(event, idx)}
          className={cx(
            controlSurfaceClasses(isInvalid),
            'h-14.5 w-14.5 text-center text-display-s font-normal md:h-20.5 md:w-20.5 md:text-display-xl',
          )}
        />
      ))}
    </div>
  );
};
